import React, { useState, useEffect } from 'react';
import { investmentPlanAPI } from '../api';
import LoadingSpinner from './LoadingSpinner';

const UserInvestmentPlans = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [claimingReward, setClaimingReward] = useState(false);

  // Investment plan configurations
  const investmentPlans = {
    '15000_1000_3': { investment: 15000, reward: 1000, duration: 3, name: 'Basic Plan' },
    '25000_3000_3': { investment: 25000, reward: 3000, duration: 3, name: 'Silver Plan' },
    '50000_5000_3': { investment: 50000, reward: 5000, duration: 3, name: 'Gold Plan' },
    '100000_6000_6': { investment: 100000, reward: 6000, duration: 6, name: 'Platinum Plan' },
    '250000_15000_6': { investment: 250000, reward: 15000, duration: 6, name: 'Diamond Plan' }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await investmentPlanAPI.getUserDashboard();
      if (response.data.success) {
        const data = response.data.data;
        setDashboardData(data);
        if (data.planAutoCreated) {
          alert('You qualified for an investment plan — it has been activated automatically. You can claim monthly rewards below.');
        }
      }
      setError('');
    } catch (err) {
      setError('Failed to fetch investment plans data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimReward = async (planId) => {
    if (!window.confirm('Are you sure you want to claim this reward?')) {
      return;
    }

    try {
      setClaimingReward(true);
      setError('');
      const response = await investmentPlanAPI.claimReward({ planId });
      
      console.log('Claim reward response:', response);
      
      if (response.data.success) {
        alert(`Reward claimed successfully! Amount: ${formatCurrency(response.data.data.amount)}`);
        fetchDashboardData(); // Refresh data
      } else {
        const errorMsg = response.data.error || 'Failed to claim reward';
        setError(errorMsg);
        alert(`Error: ${errorMsg}`);
      }
    } catch (err) {
      console.error('Claim reward error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to claim reward';
      setError(errorMsg);
      alert(`Error: ${errorMsg}`);
    } finally {
      setClaimingReward(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'text-green-600 bg-green-100';
      case 'COMPLETED': return 'text-blue-600 bg-blue-100';
      case 'CANCELLED': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const canClaimReward = (plan) => {
    if (plan.status !== 'ACTIVE') return false;
    
    // Check if user has already claimed any reward at all
    if (dashboardData.rewardHistory && dashboardData.rewardHistory.length > 0) return false;
    
    const currentMonth = Math.floor(plan.totalRewardsPaid / plan.monthlyReward) + 1;
    return currentMonth <= plan.durationMonths;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Investment Plans</h1>
        <p className="text-gray-600">Manage your investment plans and claim monthly rewards</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {dashboardData && (
        <>
          {/* User Business Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">Total Direct Business</h3>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(dashboardData.user.totalDirectBusiness)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">Referral Business</h3>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(dashboardData.user.referralBusiness)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">Own Business</h3>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(dashboardData.user.ownBusiness)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">Direct Referrals</h3>
              <p className="text-2xl font-bold text-purple-600">
                {dashboardData.user.directSponseesCount}
              </p>
            </div>
          </div>

          {/* Eligible Plans */}
          {dashboardData.eligiblePlans.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold text-green-800 mb-4">🎉 Your investment plan tier</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dashboardData.eligiblePlans.map((plan) => (
                  <div key={plan.type} className="bg-white rounded-lg p-4 border border-green-300">
                    <h3 className="font-bold text-lg text-green-800 mb-2">{plan.name}</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Investment:</span>
                        <span className="font-semibold">{formatCurrency(plan.investment)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Monthly Reward:</span>
                        <span className="font-semibold text-green-600">{formatCurrency(plan.reward)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Duration:</span>
                        <span className="font-semibold">{plan.duration} months</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Return:</span>
                        <span className="font-bold text-green-700">
                          {formatCurrency(plan.reward * plan.duration)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 text-sm text-green-700 font-medium">
                      ✅ Based on your direct business, this is your highest eligible tier. When you open this page, the plan is created automatically if you do not already have it active.
                    </div>
                    <div className="mt-2 text-xs text-orange-600 font-medium">
                      ⚠️ IMPORTANT: You can claim only ONE reward total across all plans. Choose wisely!
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Use &quot;Claim Reward&quot; in the table below to claim your one-time reward.
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Investment Plans */}
          <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">My Investment Plans</h2>
            </div>
            {dashboardData.investmentPlans.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <div className="mb-4">
                  <div className="text-6xl mb-2">💼</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Investment Plans</h3>
                  <p className="text-gray-600">
                    Increase your direct business to qualify for investment plans
                  </p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rewards Progress
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dashboardData.investmentPlans.map((plan) => (
                      <tr key={plan._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {investmentPlans[plan.planType]?.name || plan.planType}
                            </div>
                            <div className="text-sm text-gray-500">
                              Investment: {formatCurrency(plan.investmentAmount)}
                            </div>
                            <div className="text-sm text-gray-500">
                              Monthly: {formatCurrency(plan.monthlyReward)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {plan.durationMonths} months
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(plan.endDate).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(plan.status)}`}>
                            {plan.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm text-gray-900">
                              {formatCurrency(plan.totalRewardsPaid)} / {formatCurrency(plan.monthlyReward * plan.durationMonths)}
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div
                                className="bg-green-500 h-2 rounded-full"
                                style={{
                                  width: `${Math.min((plan.totalRewardsPaid / (plan.monthlyReward * plan.durationMonths)) * 100, 100)}%`
                                }}
                              ></div>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {Math.floor(plan.totalRewardsPaid / plan.monthlyReward)} of {plan.durationMonths} months claimed
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {canClaimReward(plan) && (
                            <button
                              onClick={() => handleClaimReward(plan._id)}
                              disabled={claimingReward}
                              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                            >
                              {claimingReward ? 'Claiming...' : 'Claim Reward'}
                            </button>
                          )}
                          {plan.status === 'COMPLETED' && (
                            <span className="text-blue-600 font-medium">Completed</span>
                          )}
                          {plan.status === 'CANCELLED' && (
                            <span className="text-red-600 font-medium">Cancelled</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Reward History */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Reward History</h2>
            </div>
            {dashboardData.rewardHistory.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No rewards claimed yet
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Month
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dashboardData.rewardHistory.map((reward) => (
                      <tr key={reward._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          Month {reward.month}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {reward.planId ? (investmentPlans[reward.planId.planType]?.name || reward.planId.planType) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                          {formatCurrency(reward.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(reward.status)}`}>
                            {reward.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(reward.rewardDate || reward.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserInvestmentPlans;
